'use client';

import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';

import { cn } from '@/lib/utils';

type FieldContextValue = {
  labelId: string;
  descriptionId: string;
};

const FieldContext = React.createContext<FieldContextValue | null>(null);

const Field = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => {
  const labelId = React.useId();
  const descriptionId = React.useId();

  return (
    <FieldContext.Provider value={{ labelId, descriptionId }}>
      <div
        ref={ref}
        aria-describedby={descriptionId}
        aria-labelledby={labelId}
        className={cn(className)}
        data-slot="field"
        role="group"
        {...props}
      />
    </FieldContext.Provider>
  );
});
Field.displayName = 'Field';

type FieldTitleProps = React.HTMLAttributes<HTMLHeadingElement> & {
  as?: 'h2' | 'h3';
};

const FieldTitle = React.forwardRef<HTMLHeadingElement, FieldTitleProps>(
  ({ className, id: idProp, as: Component = 'h2', ...props }, ref) => {
    const context = React.useContext(FieldContext);
    const id = idProp ?? context?.labelId;

    if (id == null && !context) {
      throw new Error('FieldTitle must be used within Field');
    }

    return <Component ref={ref} className={className} id={id ?? undefined} {...props} />;
  }
);
FieldTitle.displayName = 'FieldTitle';

type FieldDescriptionProps = React.HTMLAttributes<HTMLParagraphElement> & {
  asChild?: boolean;
};

const FieldDescription = React.forwardRef<HTMLParagraphElement, FieldDescriptionProps>(
  ({ className, id: idProp, asChild = false, ...props }, ref) => {
    const context = React.useContext(FieldContext);
    const id = idProp ?? context?.descriptionId;

    if (id == null && !context) {
      throw new Error('FieldDescription must be used within Field');
    }

    const mergedClassName = cn('text-sm text-greyscale-1000', className);

    if (asChild) {
      return <Slot ref={ref} className={mergedClassName} id={id ?? undefined} {...props} />;
    }

    return <p ref={ref} className={mergedClassName} id={id ?? undefined} {...props} />;
  }
);
FieldDescription.displayName = 'FieldDescription';

export type { FieldDescriptionProps };
export { Field, FieldDescription, FieldTitle };
